//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { PinterestGatewayUser } from '@bvengineering/pinterest-gateway-service-types';

import {
  useConnectedFacebookPageTokenQuery,
  useConnectedInstagramAccountQuery,
  useGetYouTubeUserQuery,
  usePinterestUserQuery,
  useTikTokUserQuery,
  useTwitterSettingsQuery,
} from '@account-settings/accountSettingsQueries';
import { useConnectionStatusQuery } from '@account-settings/components/social-media-accounts/tiktok/TikTokAdsQueries';
import { useGetYouTubeConnectionStatusQuery } from '@account-settings/components/social-media-accounts/youtube/youtubeAccountSettingsQueries';
import { IConnectedBrandInstagramBusinessAccount } from '@apis/instagram-settings/gatewayInstagramSettingsApiModel';
import { ITikTokUser } from '@apis/tiktok-user/tiktokUserApiModel';
import { ITwitterSettingsResponse } from '@apis/twitter-settings/twitterSettingsApiModel';
import { YouTubeUser } from '@apis/youtube/youtubeApiModel';
import IFacebookPageToken from '@models/IFacebookPageToken';
import { SocialNetwork } from '@models/SocialNetwork';
import { usePinterestConnectionStatusQuery } from '@reporting/queries/reportingQueries';
import { isDefined, isTruthy } from '@utils/filterPredicates';

export interface PinterestConnection {
  credentials?: PinterestGatewayUser;
  isConnected: boolean;
  isLoading: boolean;
  hasStaleCredentials?: boolean;
}

export interface FacebookConnection {
  credentials?: IFacebookPageToken;
  isConnected: boolean;
  isLoading: boolean;
  hasStaleCredentials?: boolean;
}

export interface InstagramConnection {
  credentials?: IConnectedBrandInstagramBusinessAccount;
  isConnected: boolean;
  isLoading: boolean;
  hasStaleCredentials?: boolean;
}

export interface YouTubeConnection {
  credentials?: YouTubeUser;
  isConnected: boolean;
  isLoading: boolean;
  hasStaleCredentials?: boolean;
}

export interface TikTokConnection {
  credentials?: ITikTokUser;
  isConnected: boolean;
  isLoading: boolean;
  hasStaleCredentials?: boolean;
}

export interface TwitterConnection {
  credentials?: ITwitterSettingsResponse;
  isConnected: boolean;
  isLoading: boolean;
  hasStaleCredentials?: boolean;
}

export type SocialConnection =
  | PinterestConnection
  | FacebookConnection
  | InstagramConnection
  | YouTubeConnection
  | TikTokConnection
  | TwitterConnection;

export interface SocialConnections {
  [SocialNetwork.Pinterest]: PinterestConnection;
  [SocialNetwork.Facebook]: FacebookConnection;
  [SocialNetwork.Instagram]: InstagramConnection;
  [SocialNetwork.YouTube]: YouTubeConnection;
  [SocialNetwork.TikTok]: TikTokConnection;
  [SocialNetwork.Twitter]: TwitterConnection;
}

/**
 * A hook that fetches the connection status and user data for all social networks.
 *
 * @returns An object containing the connection status and user data for each social network.
 */
export const useSocialConnections = (): { connections: SocialConnections } => {
  // Fetching Pinterest user data
  const {
    data: pinterestConnectionStatus,
    isLoading: isPinterestConnectionStatusLoading,
  } = usePinterestConnectionStatusQuery();

  const isPinterestConnected = isTruthy(pinterestConnectionStatus?.isConnected);
  const isPinterestStale = isTruthy(
    pinterestConnectionStatus?.hasStaleCredentials,
  );

  const { data: pinterestUser, isLoading: isPinterestUserLoading } =
    usePinterestUserQuery(isPinterestConnected);

  // Fetching connected Facebook page token
  const {
    data: connectedFacebookPageToken,
    isLoading: isFacebookConnectionStatusLoading,
  } = useConnectedFacebookPageTokenQuery();

  const facebookPageToken = connectedFacebookPageToken?.data.facebookPageToken;
  const isFacebookConnected = isDefined(
    connectedFacebookPageToken?.data.facebookPageToken,
  );

  const { data: connectedInstagramAccount, isLoading: isInstagramLoading } =
    useConnectedInstagramAccountQuery();

  const instagramAccount = connectedInstagramAccount?.data.connectedAccount;
  const isInstagramConnected = isDefined(instagramAccount);

  // Fetching YouTube user data
  const { data: youtubeUser, isLoading: isYoutubeLoading } =
    useGetYouTubeUserQuery();

  const {
    data: youtubeConnectionInfo,
    isLoading: isLoadingYouTubeConnectionInfo,
  } = useGetYouTubeConnectionStatusQuery();

  const isYoutubeConnected = isDefined(youtubeUser);
  const isYoutubeStale = isTruthy(youtubeConnectionInfo?.hasStaleCredentials);

  // Fetching Twitter user data
  const { data: twitterSettings, isLoading: isTwitterLoading } =
    useTwitterSettingsQuery();

  const isTwitterConnected = isDefined(twitterSettings?.account);

  // Fetching tiktok user data
  const { data: tiktokUser, isLoading: isTiktokUserLoading } =
    useTikTokUserQuery();

  const {
    data: tiktokConnectionStatus,
    isLoading: isTiktokConnectionStatusLoading,
  } = useConnectionStatusQuery();

  const isTiktokConnected = isDefined(tiktokUser);
  const isTiktokStale = isTruthy(tiktokConnectionStatus?.hasStaleCredentials);

  return {
    connections: {
      [SocialNetwork.Pinterest]: {
        hasStaleCredentials: isPinterestStale,
        isLoading:
          isPinterestConnectionStatusLoading ||
          (isPinterestConnected && isPinterestUserLoading),
        isConnected: isPinterestConnected,
        credentials: pinterestUser,
      },
      [SocialNetwork.Facebook]: {
        isLoading: isFacebookConnectionStatusLoading,
        isConnected: isFacebookConnected,
        credentials: facebookPageToken,
      },
      [SocialNetwork.Instagram]: {
        isLoading: isInstagramLoading,
        isConnected: isInstagramConnected,
        credentials: instagramAccount,
      },
      [SocialNetwork.YouTube]: {
        hasStaleCredentials: isYoutubeStale,
        isLoading: isYoutubeLoading || isLoadingYouTubeConnectionInfo,
        isConnected: isYoutubeConnected,
        credentials: youtubeUser,
      },
      [SocialNetwork.TikTok]: {
        hasStaleCredentials: isTiktokStale,
        isLoading: isTiktokUserLoading || isTiktokConnectionStatusLoading,
        isConnected: isTiktokConnected,
        credentials: tiktokUser,
      },
      [SocialNetwork.Twitter]: {
        isLoading: isTwitterLoading,
        isConnected: isTwitterConnected,
        credentials: twitterSettings,
      },
    },
  };
};
